import React from "react";
import { Box, CircularProgress, Tooltip } from "@mui/material";
import { RequestQuote } from "@mui/icons-material";
import { useCancellationHistoryByPatient } from "../../../stores/Appointment/appointment.queries";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../ui/table";
import { mobiliTheme } from "../../../themes/mobiliTheme";
import { getColorForRxNumber } from "../../../helpers/statusColor";
import { formatDateUI, formatTime } from "../../../helpers/dateFormat";

interface CancellationHistoryTableProps {
  patientId: number;
  /** When false the underlying query is not fetched (e.g. dialog closed). */
  enabled?: boolean;
  /**
   * When set, constrains the table to this height (px) and makes the wrapper
   * the single scroll container for both axes, keeping the header sticky.
   */
  maxHeight?: number | string;
}

/**
 * Dense cells — this is a data table, not a settings page. Values are short and
 * atomic, so they do not wrap; the container scrolls instead.
 */
const CELL = "px-1 py-1 align-top";

/**
 * `h-auto` is required: shadcn's TableHead ships `h-12`, a 48px header row, and
 * unlike the padding that height does not merge away.
 *
 * The header is sticky and therefore needs its own opaque fill — it scrolls
 * over the rows underneath it.
 */
const HEAD =
  "sticky top-0 z-10 h-auto bg-card px-1 py-1 text-left text-micro font-semibold uppercase text-ink-muted whitespace-nowrap";

/**
 * "fristgerecht ( > 24 st. vorher)" without the parenthetical, which defines
 * the term rather than adding to it — and costs this column ~70px, enough to
 * push the whole table into a horizontal scroll on a 1280px window. The full
 * value stays on the tooltip.
 */
const shortTimeliness = (timeliness: string): string => timeliness.split(" (")[0].trim();

/**
 * Renders the full cancellation history (resolved and unresolved) for a patient
 * as a table, making it explicit who initiated each cancellation and what the
 * relevant metadata (timeliness, invoicing) was. Used both by the standalone
 * history popup and inline inside the cancel-appointment dialog.
 *
 * Every value is plain text (design-system: a pill in a table cell draws a box
 * around something the grid has already grouped). The only colour is the status
 * dot, where the colour IS the value.
 */
export default function CancellationHistoryTable({
  patientId,
  enabled = true,
  maxHeight,
}: CancellationHistoryTableProps) {
  const { data, isLoading, isError } = useCancellationHistoryByPatient(patientId, enabled);
  const history = data ?? [];

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", py: 4 }}>
        <CircularProgress size={28} />
      </Box>
    );
  }

  if (isError) {
    return (
      <div className="py-6 text-center text-dense text-destructive">
        Stornierungsverlauf konnte nicht geladen werden.
      </div>
    );
  }

  if (history.length === 0) {
    return (
      <div className="py-6 text-center text-dense text-ink-muted">
        Keine Stornierungen vorhanden.
      </div>
    );
  }

  return (
    <div style={{ maxHeight, overflow: "auto" }}>
      {/*
        Font size inline rather than `className="text-dense"`: shadcn's Table
        sets `text-sm` and merges through `cn()` → tailwind-merge, which does
        not know our custom text steps, so both survive and `text-sm` wins on
        output order — and its 20px line-height holds every row open.
      */}
      <Table style={{ fontSize: "var(--text-dense)", lineHeight: 1.25 }}>
        <TableHeader>
          <TableRow className="border-0 border-b border-solid border-ink-line-strong hover:bg-transparent">
            <TableHead className={HEAD}>Datum</TableHead>
            <TableHead className={HEAD}>Rezept</TableHead>
            <TableHead className={HEAD}>Therapie</TableHead>
            <TableHead className={HEAD}>Von wem</TableHead>
            <TableHead className={HEAD}>Grund</TableHead>
            <TableHead className={HEAD}>Rechtzeitigkeit</TableHead>
            <TableHead className={HEAD}>Status</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {history.map((item) => {
            const rxs = item.rxs ?? [];
            const therapies = rxs.flatMap((rx) => rx.therapies ?? []);
            return (
              <TableRow
                key={item.id}
                className="border-0 border-b border-solid border-ink-line-faint hover:bg-ink-band"
              >
                <TableCell className={`${CELL} whitespace-nowrap`}>
                  <div className="font-semibold tabular-nums text-ink-text">
                    {item.originalDate ? formatDateUI(item.originalDate) : "—"}
                  </div>
                  {item.originalDate && (
                    <div className="text-mini tabular-nums text-ink-muted">
                      {formatTime(item.originalDate)}
                    </div>
                  )}
                </TableCell>

                {/* The Rx colour as a rail, the way every other surface carries
                    it — not as a filled chip around the number. */}
                <TableCell className={`${CELL} whitespace-nowrap`}>
                  {rxs.length > 0
                    ? rxs.map((rx, idx) => (
                        <div
                          key={`${item.id}-rx-${idx}`}
                          className="border-0 border-l-4 border-solid pl-1.5"
                          style={{
                            borderLeftColor: rx.rxNumber
                              ? getColorForRxNumber(rx.rxNumber)
                              : "transparent",
                          }}
                        >
                          <div className="font-semibold tabular-nums text-ink-text">
                            {rx.rxNumber || "—"}
                          </div>
                          {rx.date && (
                            <div className="text-mini tabular-nums text-ink-muted">
                              {formatDateUI(rx.date)}
                            </div>
                          )}
                        </div>
                      ))
                    : "—"}
                </TableCell>

                <TableCell className={`${CELL} font-semibold text-ink-text`}>
                  {therapies.length > 0 ? therapies.join(", ") : "—"}
                </TableCell>

                <TableCell className={`${CELL} whitespace-nowrap text-ink-text`}>
                  {item.byTherapist ? "Therapeut" : "Patient"}
                </TableCell>

                <TableCell className={`${CELL} text-ink-text`}>{item.reason || "—"}</TableCell>

                <TableCell className={`${CELL} whitespace-nowrap text-ink-text`}>
                  {/* Timeliness is only meaningful for patient-side cancellations. */}
                  {!item.byTherapist && item.timeliness ? (
                    <Tooltip title={item.timeliness}>
                      <span>{shortTimeliness(item.timeliness)}</span>
                    </Tooltip>
                  ) : (
                    "—"
                  )}
                  {item.noShowInvoice && (
                    <Tooltip title="Bei Nichterscheinen in Rechnung gestellt">
                      <RequestQuote
                        sx={{
                          fontSize: "var(--row-icon)",
                          ml: 0.5,
                          verticalAlign: "middle",
                          color: mobiliTheme.palette.error.main,
                        }}
                      />
                    </Tooltip>
                  )}
                </TableCell>

                <TableCell className={`${CELL} whitespace-nowrap text-ink-text`}>
                  <span className="flex items-center gap-1.5">
                    <span
                      className={`h-2 w-2 shrink-0 rounded-pill ${
                        item.resolved ? "bg-success" : "bg-warning"
                      }`}
                    />
                    {item.resolved ? "Verlegt" : "Offen"}
                  </span>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
